/**
 * The Help area's guides: markdown files bundled at build time and rendered
 * through `../markdown`, the same pipeline the format spec page uses.
 *
 * Adding a guide is adding a `.md` file under `guides/` and one entry here —
 * nothing about the index page, the route, or the rendering changes.
 */

import {
  AZURE_GUIDE_SLUG,
  BACKBLAZE_GUIDE_SLUG,
  DELETE_VAULT_GUIDE_SLUG,
  GOOGLE_CLOUD_GUIDE_SLUG,
  S3_GUIDE_SLUG,
  SECRETS_GUIDE_SLUG,
} from "@bigbear/shared";
import type { ComponentType } from "react";

import { BackblazeCalculator } from "./BackblazeCalculator";
import { DeleteVaultProviderNoteTable } from "./DeleteVaultProviderNoteTable";
import amazonS3 from "./guides/amazon-s3.md?raw";
import azureBlobStorage from "./guides/azure-blob-storage.md?raw";
import backblazeB2 from "./guides/backblaze-b2.md?raw";
import deleteAVaultByHand from "./guides/delete-a-vault-by-hand.md?raw";
import googleCloudStorage from "./guides/google-cloud-storage.md?raw";
import recoveryKeyAndPassphrase from "./guides/recovery-key-and-passphrase.md?raw";

export interface Guide {
  /** The route segment under `/help/`. */
  slug: string;
  /** Shown in the Help index and as the guide page's own heading. */
  title: string;
  /** One line, shown under the title in the Help index. */
  summary: string;
  /** The guide's markdown source, as bundled by Vite's `?raw` import. */
  source: string;
  /** A piece of the guide that isn't markdown — the Backblaze guide's cost
   * calculator, say. The guide page renders whatever a guide names here
   * after its article, so a guide that needs one is still just an entry in
   * this list, not a change to the page. */
  extra?: ComponentType;
}

export const GUIDES: Guide[] = [
  {
    slug: BACKBLAZE_GUIDE_SLUG,
    title: "Set up Backblaze B2",
    summary:
      "Create the account, the bucket and the key this app needs, and see what the bill actually looks like.",
    source: backblazeB2,
    extra: BackblazeCalculator,
  },
  {
    slug: GOOGLE_CLOUD_GUIDE_SLUG,
    title: "Set up Google Cloud Storage",
    summary:
      "Go from no Google Cloud account to the access key and secret this app asks for, with a budget alert and a bucket only this app can reach.",
    source: googleCloudStorage,
  },
  {
    slug: S3_GUIDE_SLUG,
    title: "Set up Amazon S3",
    summary:
      "From no AWS account to an access key that reaches one bucket, with a Budget alert set up before anything costs money.",
    source: amazonS3,
  },
  {
    slug: AZURE_GUIDE_SLUG,
    title: "Set up Azure Blob Storage",
    summary:
      "From no Azure account to a SAS token for one container, with a Budget alert set up and a storage account billed the way the app expects.",
    source: azureBlobStorage,
  },
  {
    slug: SECRETS_GUIDE_SLUG,
    title: "Your recovery key and passphrase",
    summary:
      "What each one is, why the recovery key is the one to keep, and where to put it so a dead PC doesn't take it with it.",
    source: recoveryKeyAndPassphrase,
  },
  {
    slug: DELETE_VAULT_GUIDE_SLUG,
    title: "Delete a vault by hand",
    summary:
      "What to do at the provider's own console for every kind of vault this app supports, and what deleting there actually costs and leaves behind.",
    source: deleteAVaultByHand,
    extra: DeleteVaultProviderNoteTable,
  },
];

export function guideBySlug(slug: string): Guide | undefined {
  return GUIDES.find((guide) => guide.slug === slug);
}
